import { T } from '@tolgee/react';
import React from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { zIndexes } from '../../../utils/zIndexes';

interface AIFeedbackOverlayProps {
  visible: boolean;
  onComplete?: () => void;
  // Replaces the default "Read the feedback on your device" text.
  message?: React.ReactNode;
  // 'black' hides whatever is behind the overlay entirely instead of blurring it.
  background?: 'blur' | 'black';
  // 'title' (the voting title screen) uses more of the screen's width and a
  // larger font than the default message.
  variant?: 'default' | 'title';
}

const AIFeedbackOverlay: React.FC<AIFeedbackOverlayProps> = ({
  visible,
  message,
  background = 'blur',
  variant = 'default',
}) => {
  if (!visible) return null;
  const isTitle = variant === 'title';

  return (
    <OverlayContainer>
      <BlurBackground $black={background === 'black'} />
      <ContentContainer $isTitle={isTitle}>
        <MessageText $isTitle={isTitle}>
          {message ?? (
            <T
              keyName="Read the feedback on your device"
              defaultValue="Read the feedback on your device"
            />
          )}
        </MessageText>
      </ContentContainer>
    </OverlayContainer>
  );
};

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlurBackground = styled.div<{ $black: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ $black }) =>
    $black ? theme.colors.blackPure : theme.colors.translucentBlack};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const ContentContainer = styled.div<{ $isTitle: boolean }>`
  position: relative;
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY') + 1};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  ${({ $isTitle }) =>
    $isTitle &&
    `
    width: 100%;
    box-sizing: border-box;
    padding: 40px 10%;
  `}
`;

const MessageText = styled.div<{ $isTitle: boolean }>`
  font-size: ${({ $isTitle }) => ($isTitle ? '95px' : '80px')};
  font-weight: bold;
  color: ${theme.colors.white};
  text-align: center;
  max-width: ${({ $isTitle }) => ($isTitle ? '100%' : '90%')};
  line-height: 1.2;
`;

export default AIFeedbackOverlay;
